import { ChangeDetectorRef, Directive, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { Model } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Meta } from '@df/core/meta/meta';
import { SearchClearEvent } from '@df/search/search-clear-event';
import { SearchEvent } from '@df/search/search-event';
import { SearchResult } from '@df/search/search-result';
import { SearchResultEvent } from '@df/search/search-result-event';
import { SearchService } from '@df/search/search.service';
import { Subscription } from 'rxjs';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class SearchOutletComponentDef implements OnInit, OnDestroy {
    productListing?: ProductListing;

    protected listingSubscription?: Subscription;

    /**
     * If component should hide when location changes
     */
    @Input()
    hideOnLocationChange = false;

    /**
     * If component should not be visible on search page
     */
    @Input()
    hideOnSearchPage = false;

    /**
     * Search id
     */
    @Input({
        required: true
    })
    searchId!: string;

    /**
     * Events subscription
     */
    protected searchSubscription?: Subscription;

    /**
     * Router subscription
     */
    protected routerSubscription?: Subscription;

    /**
     * Search result displayed
     */
    protected _result: SearchResult | undefined;

    /**
     * If component should be hidden
     */
    protected _isHidden = true;

    /**
     * Hide method
     */
    set isHidden(isHidden: boolean) {
        // document.body.classList.remove('search-outlet-open');
        if (isHidden !== this._isHidden) {
            this._isHidden = isHidden;
            this._ref.detectChanges();
        }
    }

    get isHidden(): boolean {
        return this._isHidden;
    }

    /**
     * Result setter
     */
    set result(result: SearchResult | undefined) {
        this._result = result;
        this.isHidden = result?.hasAny() ? false : true;
        this._ref.detectChanges();
    }

    /**
     * Result getter
     */
    get result(): SearchResult | undefined {
        return this._result;
    }

    /**
     * Router service
     */
    protected router = inject(Router);

    /**
     * Search service
     */
    protected searchService = inject(SearchService);

    /**
     * Search service
     */
    protected meta = inject(Meta);

    private productListingFactory = inject(ProductListingFactory);

    /**
     * Class constructor
     */
    constructor(protected _ref: ChangeDetectorRef) {}

    /**
     * Set listeners
     */
    ngOnInit(): void {
        // listen for matching events to sync results
        this.searchSubscription = this.searchService.subscribeToSearch((event: SearchEvent) => {
            if (this.searchId && event.searchId === this.searchId) {
                if (event instanceof SearchResultEvent) {
                    this.result = event.result;

                    if (this.result?.products?.length) {
                        if (this.productListing) {
                            this.productListing.setSourceModels(this.result.products);
                            this.productListing.applyFilters();
                        } else {
                            this.unsubscribeFromProductListing();
                            this.productListing = this.productListingFactory.create<Product<IProductDataV2>>(
                                {
                                    type: 'product',
                                    source: this.result,
                                    storage: false,
                                    custom: {
                                        pioAllowed: false
                                    }
                                },
                                this.result.products?.map(i => i.id) || []
                            );
                            this.listingSubscription = this.productListing.subscribe(() => this.onProductListingUpdate());

                            this.onProductListingUpdate();
                        }
                    } else {
                        this.unsubscribeFromProductListing();
                        this.productListing = undefined;
                    }
                } else if (event instanceof SearchClearEvent) {
                    this.unsubscribeFromProductListing();
                    this.productListing = undefined;
                    this.result = undefined;
                }
            }
        });

        // if should hide on location change then reset result
        this.routerSubscription = this.meta.routerEvents.subscribe(event => {
            if ((this.hideOnLocationChange && event instanceof NavigationStart) || !this.canShow()) {
                this.hide();
            }
        });
    }

    /**
     * Cleanup on destroy
     */
    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
        this.routerSubscription?.unsubscribe();
        this.unsubscribeFromProductListing();
    }

    /**
     * Check if be shown
     */
    canShow(): boolean {
        return !this.hideOnSearchPage || !this.isSearchPage();
    }

    hide(): void {
        this.isHidden = true;
    }

    /**
     * Check if its search page
     */
    protected isSearchPage(): boolean {
        return !!this.router.url.match(/^\/search\//);
    }

    protected onProductListingUpdate(): void {
        this._ref.detectChanges();
    }

    private unsubscribeFromProductListing(): void {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
            this.listingSubscription = undefined;
        }
        if (this.productListing) {
            this.productListing.clearSubscriptions();
        }
    }

    /**
     * Tracking function
     */
    trackByIdFn(index: number, model: Model): number | string {
        return model.id;
    }
}
